import { fetchClient } from '../lib/openapi-query';
import {
  type Goal,
  type GoalMetric,
  type GoalMetricsResponse,
  type GoalResponse,
  type GoalSection,
  type GoalsProgression,
  type GoalsResponse,
  type OnboardingGoalsRequest,
  type OnboardingGoalsResponse,
  type UserGoalsResponse
} from '../types/goals';

class GoalsService {

  /**
   * Get all available goals
   */
  async getAllGoals(): Promise<Goal[]> {
    try {
      const response = await fetchClient.GET('/mock/goals') as any;
      
      if (response.error) {
        console.error('Failed to fetch goals:', response.error);
        throw new Error(response.error.message ?? 'Unable to fetch goals. Please try again later.');
      }
      
      // Type cast the response data to match expected structure
      const goalsResponse = response.data as unknown as GoalsResponse;
      return goalsResponse?.goals ?? [];
    } catch (error) {
      console.error('Failed to fetch goals:', error);
      throw new Error('Unable to fetch goals. Please try again later.');
    }
  }

  /**
   * Get a specific goal by ID
   */
  async getGoalById(goalId: string): Promise<Goal> {
    try {
      const response = await fetchClient.GET('/mock/goals/{goalId}', {
        params: {
          path: { goalId }
        }
      }) as any;
      
      if (response.error) {
        console.error(`Failed to fetch goal ${goalId}:`, response.error);
        throw new Error(response.error.message ?? 'Unable to fetch goal details. Please try again later.');
      }
      
      const goalResponse = response.data as unknown as GoalResponse;
      return goalResponse?.goal;
    } catch (error) {
      console.error(`Failed to fetch goal ${goalId}:`, error);
      throw new Error('Unable to fetch goal details. Please try again later.');
    }
  }

  /**
   * Get metrics for a specific goal
   */
  async getGoalMetrics(goalId: string): Promise<GoalMetric[]> {
    try {
      const response = await fetchClient.GET('/mock/goals/{goalId}/metrics', {
        params: {
          path: { goalId }
        }
      }) as any;
      
      if (response.error) {
        console.error(`Failed to fetch metrics for goal ${goalId}:`, response.error);
        throw new Error(response.error.message ?? 'Unable to fetch goal metrics. Please try again later.');
      }
      
      const metricsResponse = response.data as unknown as GoalMetricsResponse;
      return metricsResponse?.goalMetrics ?? [];
    } catch (error) {
      console.error(`Failed to fetch metrics for goal ${goalId}:`, error);
      throw new Error('Unable to fetch goal metrics. Please try again later.');
    }
  }

  /**
   * Get user goals section for dashboard
   */
  async getUserGoalsSection(): Promise<GoalSection> {
    try {
      const response = await fetchClient.GET('/mock/user-goals') as any;
      
      if (response.error) {
        console.error('Failed to fetch user goals section:', response.error);
        throw new Error(response.error.message ?? 'Unable to fetch user goals. Please try again later.');
      }
      
      const userGoalsResponse = response.data as unknown as UserGoalsResponse;
      return userGoalsResponse?.section;
    } catch (error) {
      console.error('Failed to fetch user goals section:', error);
      throw new Error('Unable to fetch user goals. Please try again later.');
    }
  }

  /**
   * Get goals progression data (requires authentication)
   */
  async getGoalsProgression(): Promise<GoalsProgression> {
    try {
      const response = await fetchClient.GET('/schedule/goals') as any;
      
      if (response.error) {
        console.error('Failed to fetch goals progression:', response.error);
        throw new Error(response.error.message ?? 'Unable to fetch goals progression. Please check your authentication and try again.');
      }
      
      return response.data as unknown as GoalsProgression;
    } catch (error) {
      console.error('Failed to fetch goals progression:', error);
      throw new Error('Unable to fetch goals progression. Please check your authentication and try again.');
    }
  }

  /**
   * Get goals for product plans (requires authentication and sponsor access)
   */
  async getGoalsForProductPlans(): Promise<Goal[]> {
    try {
      const response = await fetchClient.GET('/plans/goals') as any;
      
      if (response.error) {
        console.error('Failed to fetch goals for product plans:', response.error);
        throw new Error(response.error.message ?? 'Unable to fetch product plan goals. Please check your permissions.');
      }
      
      return response.data as unknown as Goal[];
    } catch (error) {
      console.error('Failed to fetch goals for product plans:', error);
      throw new Error('Unable to fetch product plan goals. Please check your permissions.');
    }
  }

  /**
   * Get goals for onboarding process
   */
  async getOnboardingGoals(): Promise<Goal[]> {
    try {
      const response = await fetchClient.GET('/mock/onboarding/goals') as any;
      
      if (response.error) {
        console.error('Failed to fetch onboarding goals:', response.error);
        throw new Error(response.error.message ?? 'Unable to fetch onboarding goals. Please try again later.');
      }
      
      const goalsResponse = response.data as unknown as GoalsResponse;
      return goalsResponse?.goals ?? [];
    } catch (error) {
      console.error('Failed to fetch onboarding goals:', error);
      throw new Error('Unable to fetch onboarding goals. Please try again later.');
    }
  }

  /**
   * Submit selected goals during onboarding
   */
  async submitOnboardingGoals(selectedGoalIds: string[]): Promise<OnboardingGoalsResponse> {
    try {
      const request: OnboardingGoalsRequest = { selectedGoalIds };
      const response = await fetchClient.POST('/mock/onboarding/goals', {
        body: request
      }) as any;
      
      if (response.error) {
        console.error('Failed to submit onboarding goals:', response.error);
        throw new Error(response.error.message ?? 'Unable to submit goals selection. Please try again later.');
      }
      
      return response.data as unknown as OnboardingGoalsResponse;
    } catch (error) {
      console.error('Failed to submit onboarding goals:', error);
      throw new Error('Unable to submit goals selection. Please try again later.');
    }
  }

  /**
   * Get category display name
   */
  getCategoryDisplayName(category: string): string {
    const categoryMap: Record<string, string> = {
      'hydration': 'Hydration',
      'nutrition': 'Nutrition',
      'movement-or-exercise': 'Movement & Exercise',
      'mood': 'Mood',
      'stress': 'Stress Management',
      'energy': 'Energy',
      'health-symptoms': 'Health Symptoms',
      'dietary-awareness': 'Dietary Awareness',
      'digestive-health': 'Digestive Health',
      'supplements': 'Supplements',
      'sleep': 'Sleep',
      'other': 'Other'
    };
    return categoryMap[category] || category;
  }

  /**
   * Get category color class for UI styling
   */
  getCategoryColorClass(category: string): string {
    const colorMap: Record<string, string> = {
      'hydration': 'bg-blue-100 text-blue-800 border-blue-200',
      'nutrition': 'bg-green-100 text-green-800 border-green-200',
      'movement-or-exercise': 'bg-orange-100 text-orange-800 border-orange-200',
      'mood': 'bg-purple-100 text-purple-800 border-purple-200',
      'stress': 'bg-red-100 text-red-800 border-red-200',
      'energy': 'bg-yellow-100 text-yellow-800 border-yellow-200',
      'health-symptoms': 'bg-pink-100 text-pink-800 border-pink-200',
      'dietary-awareness': 'bg-emerald-100 text-emerald-800 border-emerald-200',
      'digestive-health': 'bg-teal-100 text-teal-800 border-teal-200',
      'supplements': 'bg-indigo-100 text-indigo-800 border-indigo-200',
      'sleep': 'bg-slate-100 text-slate-800 border-slate-200',
      'other': 'bg-gray-100 text-gray-800 border-gray-200'
    };
    return colorMap[category] ?? 'bg-gray-100 text-gray-800 border-gray-200';
  }

  /**
   * Get trending status display info
   */
  getTrendingDisplayInfo(trending: string): { text: string; color: string; icon: string } {
    const trendingMap: Record<string, { text: string; color: string; icon: string }> = {
      'ahead': { text: 'Ahead of target', color: 'text-green-600', icon: '🚀' },
      'upward': { text: 'Improving', color: 'text-green-500', icon: '📈' },
      'maintaining': { text: 'Steady progress', color: 'text-blue-500', icon: '➡️' },
      'slowing': { text: 'Slowing down', color: 'text-yellow-600', icon: '⚠️' },
      'declining': { text: 'Needs attention', color: 'text-red-600', icon: '📉' },
      'none': { text: 'No trend data', color: 'text-gray-500', icon: '📊' }
    };
    return trendingMap[trending] || trendingMap['none'];
  }
}

export const goalsService = new GoalsService();
export { GoalsService };